import React, { useEffect, useState } from 'react';
import { usePostHog } from 'posthog-js/react';
import styles from './BestellprozessSurvey.module.scss';

const SURVEY_ID = process.env.REACT_APP_POSTHOG_ORDER_CSAT_SURVEY_ID;
const DISMISS_EXPIRY_DAYS = 90;

// Helper function to extract priority from survey name
const extractPriority = (surveyName: string): number => {
    const match = surveyName?.match(/^\[P(\d+)\]/);
    return match ? parseInt(match[1], 10) : 999;
};

// Helper function to check if survey was completed
const isSurveyCompleted = (surveyId: string): boolean => {
    try {
        return localStorage.getItem(`posthog_survey_completed_${surveyId}`) === 'true';
    } catch (error) {
        console.error('Error checking survey completion:', error);
        return false;
    }
};

// Helper function to check if survey was dismissed
const isSurveyDismissed = (surveyId: string): boolean => {
    try {
        const dismissedAt = localStorage.getItem(`posthog_survey_dismissed_${surveyId}`);

        if (!dismissedAt) {
            return false;
        }

        // Handle legacy 'true' value (backward compatibility)
        if (dismissedAt === 'true') {
            return true; // Keep permanently dismissed
        }

        // Check if 90 days have passed
        const dismissedDate = new Date(dismissedAt);
        const now = new Date();
        const daysSinceDismissed = (now.getTime() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);

        if (daysSinceDismissed >= DISMISS_EXPIRY_DAYS) {
            // 90 days passed, remove dismissal and allow showing
            localStorage.removeItem(`posthog_survey_dismissed_${surveyId}`);
            return false;
        }

        return true; // Still within 90 days
    } catch (error) {
        console.error('Error checking survey dismissal:', error);
        return false;
    }
};

// Helper function to mark survey as completed
const markSurveyCompleted = (surveyId: string): void => {
    try {
        localStorage.setItem(`posthog_survey_completed_${surveyId}`, 'true');
    } catch (error) {
        console.error('Error marking survey as completed:', error);
    }
};

// Helper function to mark survey as dismissed
const markSurveyDismissed = (surveyId: string): void => {
    try {
        const dismissedAt = new Date().toISOString();
        localStorage.setItem(`posthog_survey_dismissed_${surveyId}`, dismissedAt);
    } catch (error) {
        console.error('Error marking survey as dismissed:', error);
    }
};

// Helper function to set session flag with survey ID
const setSessionSurvey = (surveyId: string): void => {
    try {
        sessionStorage.setItem('posthog_survey_shown_this_session', surveyId);
    } catch (error) {
        console.error('Error setting session survey:', error);
    }
};

// Helper function to get the survey ID shown this session
const getSessionSurvey = (): string | null => {
    try {
        return sessionStorage.getItem('posthog_survey_shown_this_session');
    } catch (error) {
        console.error('Error getting session survey:', error);
        return null;
    }
};

// Helper function to check if we should show a survey
const shouldShowSurvey = (surveyId: string): boolean => {
    const sessionSurveyId = getSessionSurvey();

    // If no survey shown this session, allow showing
    if (!sessionSurveyId) {
        return true;
    }

    // If same survey was shown, don't show again
    if (sessionSurveyId === surveyId) {
        return false;
    }

    // If different survey was shown, check if that survey was completed/dismissed
    // If it was, allow showing the new survey
    const previousSurveyCompleted = isSurveyCompleted(sessionSurveyId) || isSurveyDismissed(sessionSurveyId);
    return previousSurveyCompleted;
};

const emojis = [
    { value: 1, emoji: '😞', label: 'Sehr unzufrieden' },
    { value: 2, emoji: '😕', label: 'Unzufrieden' },
    { value: 3, emoji: '😐', label: 'Neutral' },
    { value: 4, emoji: '🙂', label: 'Zufrieden' },
    { value: 5, emoji: '😄', label: 'Sehr zufrieden' },
];

export const BestellprozessSurvey: React.FC = () => {
    const posthog = usePostHog();
    const [isVisible, setIsVisible] = useState(false);
    const [selectedRating, setSelectedRating] = useState<number | null>(null);
    const [feedbackText, setFeedbackText] = useState('');
    const [contactConsent, setContactConsent] = useState(false);
    const [surveyData, setSurveyData] = useState<any>(null);
    const [isSubmitted, setIsSubmitted] = useState(false);

    useEffect(() => {
        // Reset survey data to prevent showing stale data
        setSurveyData(null);
        setIsVisible(false);

        const timer = setTimeout(() => {
            posthog.getActiveMatchingSurveys((surveys) => {
                console.log('Fetched surveys:', surveys);

                // Debug: Check localStorage state for each survey
                surveys.forEach((survey) => {
                    console.log(`Survey ${survey.name}:`, {
                        id: survey.id,
                        completed: isSurveyCompleted(survey.id),
                        dismissed: isSurveyDismissed(survey.id),
                        localStorageKey: `posthog_survey_dismissed_${survey.id}`,
                        localStorageValue: localStorage.getItem(`posthog_survey_dismissed_${survey.id}`),
                    });
                });

                // Filter out completed and dismissed surveys
                const availableSurveys = surveys.filter((survey) => !isSurveyCompleted(survey.id) && !isSurveyDismissed(survey.id));

                console.log(
                    'Available surveys after filtering:',
                    availableSurveys.map((s) => ({
                        id: s.id,
                        name: s.name,
                        priority: extractPriority(s.name),
                    }))
                );

                if (availableSurveys.length === 0) {
                    console.log('No available surveys to show');
                    return;
                }

                // Sort by priority (lower number = higher priority)
                const sortedSurveys = availableSurveys.sort((a, b) => {
                    const priorityA = extractPriority(a.name);
                    const priorityB = extractPriority(b.name);
                    return priorityA - priorityB;
                });

                // Find first survey that passes session check
                const selectedSurvey = sortedSurveys.find((survey) => shouldShowSurvey(survey.id));

                if (!selectedSurvey) {
                    console.log('No surveys available to show this session');
                    return;
                }

                console.log('Selected survey:', selectedSurvey.name, 'Priority:', extractPriority(selectedSurvey.name));
                console.log('Showing survey:', selectedSurvey.name);
                setSurveyData(selectedSurvey);
                setIsVisible(true);
                setSessionSurvey(selectedSurvey.id);
            });
        }, 2000);

        return () => clearTimeout(timer);
    }, [posthog]);

    const handleRatingClick = (rating: number) => {
        setSelectedRating(rating);
    };

    const handleSubmit = () => {
        if (!selectedRating) {
            return;
        }

        if (surveyData && surveyData.questions) {
            const responses: Record<string, any> = {};
            const questions = surveyData.questions;

            // Dynamically find questions by type
            const ratingQuestion = questions.find((q: any) => q.type === 'rating') || questions[0];
            const feedbackQuestion = questions.find((q: any) => q.type === 'open') || questions[1];
            const contactQuestion = questions.find((q: any) => q.type === 'multiple_choice') || questions[2];

            // Build responses dynamically using question IDs
            if (ratingQuestion?.id) {
                responses[`$survey_response_${ratingQuestion.id}`] = selectedRating;
            }

            if (feedbackQuestion?.id && feedbackText) {
                responses[`$survey_response_${feedbackQuestion.id}`] = feedbackText;
            }

            if (contactQuestion?.id && contactConsent) {
                responses[`$survey_response_${contactQuestion.id}`] = [
                    'Ich bin mit einer Kontaktaufnahme zum Thema dieser Umfrage einverstanden.',
                ];
            }

            posthog.capture('survey sent', {
                $survey_id: surveyData.id,
                ...responses,
            });

            // Mark survey as completed
            markSurveyCompleted(surveyData.id);
        }

        setIsSubmitted(true);

        setTimeout(() => {
            setIsVisible(false);
        }, 1500);
    };

    const handleDismiss = () => {
        if (surveyData) {
            posthog.capture('survey dismissed', {
                $survey_id: surveyData.id,
            });
        }
        setIsVisible(false);
    };

    const handleDismissPermanently = () => {
        if (surveyData) {
            posthog.capture('survey dismissed', {
                $survey_id: surveyData.id,
                dismissed_permanently: true,
            });
            markSurveyDismissed(surveyData.id);
        }
        setIsVisible(false);
    };

    if (!isVisible) {
        return null;
    }

    return (
        <div className={styles._surveyOverlay}>
            <div className={styles._surveyContainer}>
                <button className={styles._closeButton} onClick={handleDismiss} aria-label="Schließen">
                    ×
                </button>

                {!isSubmitted ? (
                    <>
                        <h2 className={styles._mainTitle}>Feedback</h2>
                        <p className={styles._introText}>Dauert unter 30 Sekunden und hilft uns, den Bestellprozess zu verbessern.</p>

                        <h3 className={styles._questionTitle}>
                            Wie zufrieden sind Sie mit dem Bestellprozess in DEUPA? <span className={styles._required}>*</span>
                        </h3>
                        <div className={styles._emojiContainer}>
                            {emojis.map((item) => (
                                <button
                                    key={item.value}
                                    className={`${styles._emojiButton} ${selectedRating === item.value ? styles._selected : ''}`}
                                    onClick={() => handleRatingClick(item.value)}
                                    aria-label={item.label}
                                    title={item.label}
                                >
                                    <span className={styles._emoji}>{item.emoji}</span>
                                </button>
                            ))}
                        </div>

                        <div className={styles._feedbackSection}>
                            <label htmlFor="feedbackText" className={styles._feedbackLabel}>
                                Was können wir verbessern?
                            </label>
                            <textarea
                                id="feedbackText"
                                className={styles._feedbackTextarea}
                                value={feedbackText}
                                onChange={(e) => setFeedbackText(e.target.value.slice(0, 250))}
                                maxLength={250}
                                placeholder="Ihr Feedback (optional)"
                                rows={3}
                            />
                            <div className={styles._characterCount}>{feedbackText.length}/250</div>
                        </div>

                        <div className={styles._contactSection}>
                            <h3 className={styles._questionTitle}>Kontakt</h3>
                            <label className={styles._checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={contactConsent}
                                    onChange={(e) => setContactConsent(e.target.checked)}
                                    className={styles._checkbox}
                                />
                                <span>Ich bin mit einer Kontaktaufnahme zum Thema dieser Umfrage einverstanden.</span>
                            </label>
                        </div>

                        <div className={styles._buttonContainer}>
                            <button className={styles._dismissButton} onClick={handleDismissPermanently}>
                                Nicht erneut fragen
                            </button>
                            <button className={styles._submitButton} onClick={handleSubmit} disabled={!selectedRating}>
                                <svg
                                    className={styles._submitIcon}
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <line x1="22" y1="2" x2="11" y2="13" />
                                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                                </svg>
                                Feedback geben
                            </button>
                        </div>
                        <p className={styles._footerText}>
                            Wir verarbeiten Ihre Angaben zur Verbesserung von DEUPA. Kontaktaufnahme nur bei aktiver Einwilligung; Widerruf
                            jederzeit möglich.
                        </p>
                    </>
                ) : (
                    <p className={styles._thankYouMessage}>Vielen Dank für Ihr Feedback!</p>
                )}
            </div>
        </div>
    );
};
